/**
 * @file TestingTab.jsx
 * @description Hardware Testing — three tests, one row to start any of them:
 *
 *                INSTALLATION TEST  a unit going onto (or just onto) a
 *                                   machine. Started from the DEVICE ID;
 *                                   both data sets are checked.
 *                DEVICE TEST        the TCU on its own — GPS, battery,
 *                                   signal, IMU. Started from the DEVICE ID.
 *                MACHINE TEST       what a machine is sending. Started from
 *                                   the MACHINE NAME; the mapped TCU is
 *                                   looked up from it.
 *
 *              The tester also says which data to watch (device / machine)
 *              and whether the run should be SAVED — a saved run records the
 *              data it received; an unsaved one is watch-only and writes
 *              nothing. The identifier field is a plain box on purpose: the
 *              crew has the id in hand, and a suggestion list only gets in
 *              the way.
 *
 *              Running, the session takes the full page: the data arriving at
 *              the top, and under it whether everything the model should send
 *              has actually arrived.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, Typography, Button, Stack, Alert, Chip, TextField, Switch,
    ToggleButton, ToggleButtonGroup, FormControlLabel, Checkbox, Tooltip,
    Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
    Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import {
    Science as TestIcon,
    CheckCircle as PassIcon,
    Cancel as FailIcon,
    Build as InstallIcon,
    Memory as DeviceIcon,
    PrecisionManufacturing as MachineIcon,
    Close as CloseIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import hardwareLifecycleService from '../../../services/hardwareLifecycleService';
import TestRunPanel from './TestRunPanel';
import { formatToIST } from '../../../utils/dataUtils';

/**
 * apiClient throws `API Error: 400 Bad Request - {"success":false,"error":"…"}`.
 * The tester needs the sentence, not the envelope — "No machine matches BA22
 * — use its serial number, model or equipment id" is the whole point of the
 * server's message.
 */
const friendly = (e) => {
    const m = String(e?.message || '');
    const json = m.slice(m.indexOf('{'));
    try {
        const parsed = JSON.parse(json);
        if (parsed?.error) return parsed.error;
    } catch { /* not an envelope — fall through */ }
    return m || 'Something went wrong';
};

const fmt = (ts) => {
    if (!ts) return '—';
    const out = formatToIST(ts, 'dd MMM · HH:mm');
    return out === 'N/A' || out === 'Invalid Date' ? '—' : out;
};

/** The three tests, and what each one asks for. */
const TEST_TYPES = [
    {
        key: 'INSTALL', label: 'Installation test', icon: <InstallIcon sx={{ fontSize: 17 }} />,
        field: 'Device ID', placeholder: 'IMEI of the unit being installed',
        scopes: ['device', 'machine'],
        blurb: 'A unit going onto a machine — both the TCU’s own data and the machine’s.',
    },
    {
        key: 'DEVICE', label: 'Device test', icon: <DeviceIcon sx={{ fontSize: 17 }} />,
        field: 'Device ID', placeholder: 'IMEI of the TCU',
        scopes: ['device'],
        blurb: 'The TCU on its own — GPS, device battery, network signal, IMU.',
    },
    {
        key: 'MACHINE', label: 'Machine test', icon: <MachineIcon sx={{ fontSize: 17 }} />,
        field: 'Machine name', placeholder: 'Serial number, model or equipment id',
        scopes: ['machine'],
        blurb: 'What the machine is sending — its CAN readings against the model’s frame list.',
    },
];

const typeOf = (key) => TEST_TYPES.find(t => t.key === key) || TEST_TYPES[0];

const TestingTab = ({ presetDevice }) => {
    const [testType, setTestType] = useState('INSTALL');
    const [identifier, setIdentifier] = useState(presetDevice || '');
    const [scopes, setScopes] = useState(typeOf('INSTALL').scopes);
    const [save, setSave] = useState(true);

    const [session, setSession] = useState(null);   // {id|null, device_id, equipment_id, scopes, saved}
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewing, setViewing] = useState(null);   // a saved session's captured data

    const load = useCallback(async () => {
        try {
            const h = await hardwareLifecycleService.listTests(20);
            setHistory(h.data || []);
            setError('');
        } catch (e) {
            setError(friendly(e));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (presetDevice) setIdentifier(presetDevice); }, [presetDevice]);

    // Switching the test resets the data selection to what that test is for —
    // the tester can still tick the other one back on.
    const pickType = (key) => {
        if (!key) return;
        setTestType(key);
        setScopes(typeOf(key).scopes);
    };

    const toggleScope = (which) => setScopes(prev =>
        prev.includes(which) ? prev.filter(s => s !== which) : [...prev, which]);

    const start = async () => {
        if (!identifier.trim()) { setError(`${typeOf(testType).field} is required`); return; }
        if (!scopes.length) { setError('Pick at least one kind of data to test'); return; }
        setBusy(true); setError('');
        try {
            const r = await hardwareLifecycleService.startTest({
                identifier: identifier.trim(), test_type: testType, scopes, save,
            });
            setSession({
                id: r.id, device_id: r.device_id, equipment_id: r.equipment_id,
                scopes: r.scopes || scopes, saved: r.saved, test_type: r.test_type,
            });
            setNotes('');
            if (r.saved) await load();
        } catch (e) {
            setError(friendly(e));
        } finally {
            setBusy(false);
        }
    };

    const finish = async (action) => {
        // An unsaved run has nothing to write — closing it just leaves the view.
        if (!session.saved) { setSession(null); return; }
        setBusy(true); setError('');
        try {
            await hardwareLifecycleService.finishTest(session.id, action, notes);
            setSession(null);
            await load();
        } catch (e) {
            setError(friendly(e));
        } finally {
            setBusy(false);
        }
    };

    const openSaved = async (id) => {
        try {
            const r = await hardwareLifecycleService.getTest(id);
            setViewing(r.data);
        } catch (e) {
            setError(friendly(e));
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

    const active = typeOf(testType);

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

            {!session ? (
                <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>Start a test session</Typography>

                    {/* The three tests, the one identifier that test needs, and go —
                        all on one line. */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                        <ToggleButtonGroup exclusive value={testType} onChange={(_, v) => pickType(v)} size="small"
                            sx={{ flexShrink: 0, '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.75, gap: 0.75 } }}>
                            {TEST_TYPES.map(t => (
                                <ToggleButton key={t.key} value={t.key}>{t.icon}{t.label}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        <TextField
                            label={active.field}
                            placeholder={active.placeholder}
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') start(); }}
                            size="small"
                            sx={{ flex: 1, minWidth: 220 }}
                        />

                        <Button variant="contained" onClick={start} disabled={busy}
                            startIcon={<TestIcon />} sx={{ fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap' }}>
                            Start test
                        </Button>
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1.5 }}>
                        {active.blurb}
                    </Typography>

                    {/* What to watch, and whether to keep it. */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 3 }} alignItems={{ sm: 'center' }}
                        sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" fontWeight={800} color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Test which data
                        </Typography>
                        <Tooltip title="GPS fix, device battery, network signal, IMU — the TCU's own health">
                            <FormControlLabel
                                control={<Checkbox size="small" checked={scopes.includes('device')} onChange={() => toggleScope('device')} />}
                                label={<Typography variant="body2" fontWeight={600}>Device data</Typography>} />
                        </Tooltip>
                        <Tooltip title="The machine's CAN readings, checked against the frames its model should send">
                            <FormControlLabel
                                control={<Checkbox size="small" checked={scopes.includes('machine')} onChange={() => toggleScope('machine')} />}
                                label={<Typography variant="body2" fontWeight={600}>Machine data</Typography>} />
                        </Tooltip>

                        <Box sx={{ flex: 1 }} />

                        <Tooltip title={save
                            ? 'The session is recorded, and closing it files the data it received'
                            : 'Watch-only — nothing is written to the database'}>
                            <FormControlLabel
                                control={<Switch size="small" checked={save} onChange={(e) => setSave(e.target.checked)} />}
                                label={<Typography variant="body2" fontWeight={700}>
                                    {save ? 'Save this test' : 'Don’t save — just watch'}
                                </Typography>} />
                        </Tooltip>
                    </Stack>
                </Card>
            ) : (
                <Box sx={{ mb: 2 }}>
                    {/* Full page: data arriving on top, the model verdict under it,
                        and the session card last. */}
                    <Card variant="outlined" sx={{ borderRadius: '14px', px: 2, py: 1.25, mb: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip size="small" color="primary" icon={typeOf(session.test_type).icon}
                                label={typeOf(session.test_type).label} sx={{ fontWeight: 800 }} />
                            <Typography variant="subtitle1" fontWeight={800} sx={{ fontFamily: 'monospace' }}>
                                {session.device_id}
                            </Typography>
                            <Chip size="small" variant="outlined"
                                label={session.scopes.map(s => s === 'device' ? 'Device data' : 'Machine data').join(' + ')}
                                sx={{ fontWeight: 700 }} />
                            <Chip size="small" color={session.saved ? 'success' : 'default'} variant="outlined"
                                label={session.saved ? `Saving · session #${session.id}` : 'Not being saved'}
                                sx={{ fontWeight: 700 }} />
                        </Stack>
                    </Card>

                    <TestRunPanel deviceId={session.device_id} equipmentId={session.equipment_id} scopes={session.scopes}>
                        <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5 }}>
                            {session.saved ? (
                                <>
                                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                                        Close session #{session.id}
                                    </Typography>
                                    <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                                        fullWidth multiline minRows={3} size="small" sx={{ mb: 1.5 }}
                                        placeholder="What was tested, observations…" />
                                    <Stack direction="row" spacing={1}>
                                        <Button color="success" variant="contained" onClick={() => finish('pass')} disabled={busy}
                                            startIcon={<PassIcon />} sx={{ fontWeight: 700, textTransform: 'none' }}>
                                            Pass
                                        </Button>
                                        <Button color="error" variant="outlined" onClick={() => finish('fail')} disabled={busy}
                                            startIcon={<FailIcon />} sx={{ fontWeight: 700, textTransform: 'none' }}>
                                            Fail
                                        </Button>
                                        <Button color="inherit" onClick={() => finish('abandon')} disabled={busy}
                                            sx={{ textTransform: 'none' }}>
                                            Abandon
                                        </Button>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                        Closing the session files the data received and the checks exactly as they stand.
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>Watch-only run</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        Nothing is being recorded. Start the test again with “Save this test” on if you want the data kept.
                                    </Typography>
                                    <Button variant="outlined" onClick={() => setSession(null)} sx={{ fontWeight: 700, textTransform: 'none' }}>
                                        End test
                                    </Button>
                                </>
                            )}
                        </Card>
                    </TestRunPanel>
                </Box>
            )}

            <Card variant="outlined" sx={{ borderRadius: '14px', overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={800}>Recent test sessions</Typography>
                </Box>
                {history.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">No test sessions yet.</Typography>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Test</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Result</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Saved readings</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.map(t => (
                                <TableRow key={t.id} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{t.device_id}</TableCell>
                                    <TableCell>{typeOf(t.test_type).label}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {String(t.scopes || '').split(',').filter(Boolean)
                                            .map(s => s === 'device' ? 'Device' : 'Machine').join(' + ') || '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={t.status}
                                            color={t.status === 'PASSED' ? 'success'
                                                : t.status === 'FAILED' ? 'error'
                                                    : t.status === 'RUNNING' ? 'info' : 'default'}
                                            sx={{ fontWeight: 700, height: 20 }} />
                                    </TableCell>
                                    <TableCell>
                                        {t.captured_count > 0 ? (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography variant="body2" fontWeight={700}>{t.captured_count}</Typography>
                                                <IconButton size="small" onClick={() => openSaved(t.id)} title="View the saved data">
                                                    <ViewIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Stack>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell>{t.started_by || '—'}</TableCell>
                                    <TableCell>{fmt(t.ended_at || t.started_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Card>

            {/* The data a saved session recorded — the point of saving it. */}
            <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    Session #{viewing?.id} — {viewing?.device_id}
                    <Box sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => setViewing(null)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {['device_readings', 'machine_readings'].map(group => {
                        const rows = viewing?.captured?.[group] || [];
                        if (!rows.length) return null;
                        return (
                            <Box key={group} sx={{ mb: 2 }}>
                                <Typography variant="caption" fontWeight={800} color="text.secondary"
                                    sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                    {group === 'device_readings' ? 'Device data' : 'Machine data'} — {rows.length} reading(s)
                                </Typography>
                                <Table size="small">
                                    <TableBody>
                                        {rows.map(r => (
                                            <TableRow key={r.key}>
                                                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.key}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{String(r.value)}</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.disabled' }}>{fmt(r.at)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        );
                    })}
                    {viewing?.notes && (
                        <Alert severity="info" icon={false}><strong>Notes:</strong> {viewing.notes}</Alert>
                    )}
                    {!viewing?.captured?.device_readings?.length && !viewing?.captured?.machine_readings?.length && (
                        <Typography variant="body2" color="text.secondary">
                            This session recorded no readings — nothing was arriving when it was closed.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TestingTab;
